import { cn, VisuallyHidden } from '@seen/ui'
import type { JSX } from 'react'
import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul' // Using original name from vaul

const DrawerDeprecated = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>): JSX.Element => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
DrawerDeprecated.displayName = 'DrawerDeprecated'

const DrawerPortalDeprecated = DrawerPrimitive.Portal

const DrawerOverlayDeprecated = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref): JSX.Element => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-black/80', className)}
    {...props}
  />
))
DrawerOverlayDeprecated.displayName = 'DrawerOverlayDeprecated'

const DrawerContentDeprecated = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref): JSX.Element => (
  <DrawerPortalDeprecated>
    <DrawerOverlayDeprecated />
    <DrawerPrimitive.Content
      ref={ref}
      aria-describedby={undefined}
      className={cn(
        'fixed inset-x-0 bottom-0 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background',
        // Known issue with vaul where text within the drawer content is not selectable without this override
        // https://github.com/emilkowalski/vaul/issues/346
        'select-text!',
        className
      )}
      {...props}
    >
      <VisuallyHidden>
        <DrawerTitleDeprecated>Drawer</DrawerTitleDeprecated>
      </VisuallyHidden>
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortalDeprecated>
))
DrawerContentDeprecated.displayName = 'DrawerContentDeprecated'

const DrawerHeaderDeprecated = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
    {...props}
  />
)
DrawerHeaderDeprecated.displayName = 'DrawerHeaderDeprecated'

const DrawerFooterDeprecated = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
)
DrawerFooterDeprecated.displayName = 'DrawerFooterDeprecated'

const DrawerTitleDeprecated = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref): JSX.Element => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'font-semibold text-lg leading-none tracking-tight',
      className
    )}
    {...props}
  >
    {props.children}
  </DrawerPrimitive.Title>
))
DrawerTitleDeprecated.displayName = 'DrawerTitleDeprecated'

const DrawerDescriptionDeprecated = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref): JSX.Element => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  >
    {props.children}
  </DrawerPrimitive.Description>
))
DrawerDescriptionDeprecated.displayName = 'DrawerDescriptionDeprecated'

export { DrawerContentDeprecated, DrawerDeprecated }
